import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FiPlusCircle } from 'react-icons/fi';
import { format, parse, isValid } from 'date-fns';
import { Button, Dropdown, Input } from '@/components';
import CustomDateInput from '@/components/common/inputs/CustomDateInput';
import { useBts } from '@/context/btsContext/BtsContextProvider';
import { useUser } from '@/context/userContext/UserContextProvider';
import { useNavigate } from 'react-router-dom';

const ProposalSchoolForm = React.forwardRef(
  ({ btsSchool, setBtsSchool, goToNextSection, proposalId }, ref) => {
    const [logoPreview, setLogoPreview] = useState(
      btsSchool.proposal.schoolLogoUrl || '',
    );
    const [imagePath, setImagePath] = useState(btsSchool.proposal.logo || '');
    const fileInputRef = useRef(null);
    const [formError, setFormError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [showPreview, setShowPreview] = useState(false);
    const previewRef = useRef(null);

    useEffect(() => {
      setLogoPreview(btsSchool.proposal.schoolLogoUrl || '');
      setImagePath(btsSchool.proposal.logo || '');
    }, [btsSchool.proposal.schoolLogoUrl, btsSchool.proposal.logo]);

    // Multi-email state
    const [emailInput, setEmailInput] = useState('');
    const [emailList, setEmailList] = useState(() => {
      if (btsSchool.proposal.emailList && btsSchool.proposal.emailList.length > 0) {
        return btsSchool.proposal.emailList;
      }
      if (btsSchool.proposal.email) {
        return btsSchool.proposal.email.split(',').map((e) => e.trim()).filter(Boolean);
      }
      return [];
    });

    const {
      createBtsSchoolProposal,
      updateBtsSchoolProposal,
      error: btsError,
      setError: setBtsError,
    } = useBts();

    const { getSalesReps, salesReps } = useUser();
    const navigate = useNavigate();

    const requiredFields = [
      'schoolName',
      'businessManager',
      'phoneNumber',
      'booklistType',
      'salesRep',
    ];

    // Helper to format date for display (yyyy-MM-dd -> dd-MM-yyyy)
    const formatDateForDisplay = (dateString) => {
      if (!dateString) return '';
      // If already in dd-MM-yyyy format, return as is
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) return dateString;
      
      const date = new Date(dateString);
      return isValid(date) ? format(date, 'dd-MM-yyyy') : '';
    };

    // Helper to format date for backend (dd-MM-yyyy -> yyyy-MM-dd)
    const formatDateForBackend = (dateString) => {
      if (!dateString) return null;
       // If already in yyyy-MM-dd format, return as is.
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;

      const parsedDate = parse(dateString, 'dd-MM-yyyy', new Date());
      return isValid(parsedDate) ? format(parsedDate, 'yyyy-MM-dd') : null;
    };

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setBtsSchool((prev) => ({
        ...prev,
        proposal: {
          ...prev.proposal,
          [name]: value,
        },
      }));
    };

    // Sync emailList into proposal state
    const syncEmailList = useCallback((list) => {
      setBtsSchool((prev) => ({
        ...prev,
        proposal: {
          ...prev.proposal,
          emailList: list,
          email: list.join(', '),
        },
      }));
    }, [setBtsSchool]);

    const addEmail = () => {
      const trimmed = emailInput.trim();
      if (!trimmed) return;
      if (!isValidEmail(trimmed)) {
        setFieldErrors((prev) => ({ ...prev, emailList: 'Enter a valid email address.' }));
        return;
      }
      if (emailList.includes(trimmed)) {
        setFieldErrors((prev) => ({ ...prev, emailList: 'This email is already added.' }));
        return;
      }
      const updated = [...emailList, trimmed];
      setEmailList(updated);
      syncEmailList(updated);
      setEmailInput('');
      setFieldErrors((prev) => ({ ...prev, emailList: undefined }));
    };

    const removeEmail = async (index) => {
      const updated = emailList.filter((_, i) => i !== index);
      setEmailList(updated);
      syncEmailList(updated);

      // If editing an existing proposal, immediately persist the email removal to the DB
      if (proposalId) {
        try {
          const updatedProposal = {
            ...btsSchool.proposal,
            emailList: updated,
            email: updated.join(', '),
          };
          await updateBtsSchoolProposal(updatedProposal, proposalId);
        } catch (err) {
          // Revert local state if the API call fails
          setEmailList(emailList);
          syncEmailList(emailList);
          setFieldErrors((prev) => ({ ...prev, emailList: 'Failed to remove email. Please try again.' }));
        }
      }
    };

    const handleEmailInputKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        addEmail();
      }
    };

    const handleDropdownChange = (name, value) => {
      setBtsSchool((prev) => ({
        ...prev,
        proposal: {
          ...prev.proposal,
          [name]: value,
        },
      }));
    };

    const handleSalesRepSelect = (e) => {
      let selectedId;
      if (e && e.target) {
        selectedId = e.target.value;
      } else if (e && e.value !== undefined) {
        selectedId = e.value;
      }
      setBtsSchool((prev) => ({
        ...prev,
        proposal: {
          ...prev.proposal,
          salesRep: { id: selectedId ? parseInt(selectedId) : null },
        },
      }));
    };

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/jpg',
        'image/avif',
        
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file && !allowedTypes.includes(file.type)) {
        setFormError(
          'Invalid file type. Only .jpg, .jpeg, .png, and .avif files are allowed.',
        );
        fileInputRef.current.value = null;
      } else if (file && file.size > maxSize) {
        setFormError('File size should not exceed 5MB');
        fileInputRef.current.value = null;
      } else {
        setFormError('');
        setFieldErrors((prev) => ({ ...prev, logo: undefined }));
        setBtsSchool((prev) => ({
          ...prev,
          proposal: {
            ...prev.proposal,
            logoFile: file,
            schoolLogoUrl: file ? '' : prev.proposal.schoolLogoUrl, // Use schoolLogoUrl
          },
        }));
        setLogoPreview(file ? URL.createObjectURL(file) : '');
        setImagePath(file ? file.name : btsSchool.proposal.logo || '');
      }
    };

    // Clear field error on change or blur
    const handleFieldBlur = (e) => {
      const { name, value } = e.target;
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
      setFormError('');
    };

    // Email validation helper
    const isValidEmail = (email) => {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    };

    // Phone validation helper (10 digits, adjust as needed)
    const isValidPhone = (phone) => {
      return /^\d{10,}$/.test(phone.trim());
    };

    const validateForm = () => {
      let errors = {};
      for (let field of requiredFields) {
        let value = btsSchool.proposal[field];
        if (typeof value === 'string') value = value.trim();
        if (!value || value === '') {
          errors[field] = 'This field is required.';
        }
      }
      // Email list validation
      if (!emailList || emailList.length === 0) {
        errors.emailList = 'At least one email address is required.';
      }
      // Phone validation
      if (
        btsSchool.proposal.phoneNumber &&
        !isValidPhone(btsSchool.proposal.phoneNumber)
      ) {
        errors.phoneNumber = 'Enter a valid phone number (min 10 digits).';
      }
      // SalesRep id check
      if (
        !btsSchool.proposal.salesRep ||
        !btsSchool.proposal.salesRep.id ||
        btsSchool.proposal.salesRep.id === 0
      ) {
        errors.salesRep = 'Select a sales rep.';
      }

      // Logo validation
      const hasLogo = btsSchool.proposal.logoFile || btsSchool.proposal.schoolLogoUrl || btsSchool.proposal.logo;
      if (!hasLogo) {
        errors.logo = 'School logo is required.';
      }

      setFieldErrors(errors);
      
      return Object.keys(errors).length === 0;
    };

    const handleNext = async (e) => {
      e.preventDefault();
      if (!validateForm()) return;
      setFormError('');
      setFieldErrors({});
      setBtsError && setBtsError(null);

      try {
        // Convert dates to backend format
        const proposalToSubmit = {
          ...btsSchool.proposal,
          deadlineForFreeDelivery: formatDateForBackend(
            btsSchool.proposal.deadlineForFreeDelivery,
          ),
          orderBeforeDate: formatDateForBackend(
            btsSchool.proposal.orderBeforeDate,
          ),
          deliveryCommencementDate: formatDateForBackend(
            btsSchool.proposal.deliveryCommencementDate,
          ),
          expectedDeliveryDate: formatDateForBackend(
            btsSchool.proposal.expectedDeliveryDate,
          ),
        };

        let resp;
        if (proposalId) {
          // Update if id exists (from props)
          resp = await updateBtsSchoolProposal(proposalToSubmit, proposalId);
          if (resp && resp.id) {
            goToNextSection(proposalId);
          }
          return;
        } else {
          // Create if no id
          resp = await createBtsSchoolProposal({ ...btsSchool, proposal: proposalToSubmit }, e);
          if (resp && resp.id) {
            setBtsSchool((prev) => ({
              ...prev,
              proposal: {
                ...prev.proposal,
                ...resp,
              },
            }));
            goToNextSection(resp.id);
          } else {
            setFormError('Failed to create proposal. Please try again.');
          }
          return;
        }
      } catch (err) {
        setFormError(err?.message || 'An error occurred');
      }
    };

    // Fetch sales reps on mount
    useEffect(() => {
      getSalesReps();
      // eslint-disable-next-line
    }, []);

    const salesRepOptions = salesReps.map((rep) => ({
      value: rep.id,
      label: `${rep.salesRep} (${rep.firstName} ${rep.lastName})`,
      data: rep.id,
    }));

    // Modal close on outside click
    useEffect(() => {
      if (!showPreview) return;
      const handleClickOutside = (event) => {
        if (previewRef.current && !previewRef.current.contains(event.target)) {
          setShowPreview(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showPreview]);

    return (
      <>
        <form onSubmit={handleNext} ref={ref}>
          
          <div className="p-5 grid grid-cols-2 gap-4 ">
            <Input
              label="School Name"
              name="schoolName"
              placeholder="Enter school name"
              className="mb-4"
              value={btsSchool.proposal.schoolName}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              required
              error={fieldErrors.schoolName}
            />
            <Dropdown
              label="Sales Rep"
              name="salesRep"
              options={salesRepOptions}
              value={
                btsSchool.proposal.salesRep?.id !== undefined &&
                btsSchool.proposal.salesRep?.id !== null &&
                btsSchool.proposal.salesRep?.id !== 0
                  ? String(btsSchool.proposal.salesRep.id)
                  : ''
              }
              placeholder="Select Sales Rep"
              className="mb-4"
              onChange={handleSalesRepSelect}
              onBlur={handleFieldBlur}
              required
              error={fieldErrors.salesRep}
            />
            <Input
              label="Business Manager"
              name="businessManager"
              value={btsSchool.proposal.businessManager}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              placeholder="Enter business manager name"
              className="mb-4"
              required
              error={fieldErrors.businessManager}
            />
            <Input
              label="Phone Number"
              name="phoneNumber"
              value={btsSchool.proposal.phoneNumber}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              placeholder="Enter phone number"
              type="tel"
              className="mb-4"
              required
              error={fieldErrors.phoneNumber}
            />
            <Dropdown
              label="BookList Type"
              name="booklistType"
              value={btsSchool.proposal.booklistType}
              onChange={(e) =>
                handleDropdownChange('booklistType', e.value || e.target.value)
              }
              onBlur={handleFieldBlur}
              placeholder="Select Booklist Type"
              options={[
                { value: 'Booklist', label: 'Booklist' },
                { value: 'Bulk', label: 'Bulk' },
              ]}
              required
              error={fieldErrors.booklistType}
              disabled={!!proposalId}
            />
            {/* Multi-email chip input */}
            <div onKeyDown={handleEmailInputKeyDown}>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    label="Email (First email will be the primary email)"
                    name="emailInput"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Enter contact email"
                    type="text"
                    required
                    error={fieldErrors.emailList}
                  />
                </div>
                <div className="pb-2">
                  <button
                    type="button"
                    onClick={addEmail}
                    className="flex items-center justify-center text-gray-400 hover:text-gray-600"
                    title="Add email"
                  >
                    <FiPlusCircle size={36} strokeWidth={1.2} />
                  </button>
                </div>
              </div>
              {/* Email chips */}
              {emailList.length > 0 && (
                <div className="flex flex-wrap gap-2 px-2 mt-1 mb-2">
                  {emailList.map((email, index) => (
                    <span
                      key={index}
                      className="flex items-center gap-1 bg-gray-100 border border-gray-300 text-gray-700 text-sm rounded-full px-3 py-1"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeEmail(index)}
                        className="ml-1 text-gray-400 hover:text-red-500 font-bold leading-none"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <Input
              label="Created Date"
              name="createdDate"
              value={
                btsSchool.proposal.createdDate || new Date().toISOString()
                  ? new Date(
                      btsSchool.proposal.createdDate ||
                        new Date().toISOString(),
                    ).toLocaleDateString()
                  : ''
              }
              required
              placeholder="Enter created date"
              type="text"
              disabled
            />
            <div>
              <Input
                type="file"
                label="Logo"
                name="logo"
                placeholder="Enter logo"
                className="mb-4"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                required={!proposalId}
                error={fieldErrors.logo}
              />
              <div className="mt-8">
                {(logoPreview || btsSchool.proposal.schoolLogoUrl) && (
                  <div className="col-span-2 flex items-center gap-2 mb-1">
                    {/* Show Preview button if preview or logoUrl */}
                    {(logoPreview || btsSchool.proposal.schoolLogoUrl) && (
                      <span
                        className="text-blue-500 cursor-pointer"
                        onClick={() => setShowPreview(true)}>
                        Preview Image
                      </span>
                    )}
                    {/* Show link only if schoolLogoUrl is present and no uploaded logo */}
                    {!logoPreview && btsSchool.proposal.schoolLogoUrl && (
                      <a
                        href={btsSchool.proposal.schoolLogoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline text-xs">
                        View Logo Link
                      </a>
                    )}
                    {showPreview && (
                      <div className="fixed inset-0 z-10 flex items-center justify-center bg-opacity-50 backdrop-blur-sm">
                        <div
                          ref={previewRef}
                          className="p-4 rounded-lg backdrop-blur-lg bg-white">
                          <img
                            src={
                              logoPreview || btsSchool.proposal.schoolLogoUrl
                            }
                            alt="School Logo"
                            className="max-h-[70vh] max-w-[90vw] object-contain"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-2 mt-6">
              <div className="bg-gray-200 p-2 mb-4 font-semibold text-lg">
                Add Parents instruction
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CustomDateInput
                  label="Deadline for free delivery"
                  name="deadlineForFreeDelivery"
                  value={formatDateForDisplay(
                    btsSchool.proposal.deadlineForFreeDelivery,
                  )}
                  onChange={(date) =>
                    handleInputChange({
                      target: { name: 'deadlineForFreeDelivery', value: date },
                    })
                  }
                  error={fieldErrors.deadlineForFreeDelivery}
                />
                <CustomDateInput
                  label="Order before date"
                  name="orderBeforeDate"
                  value={formatDateForDisplay(btsSchool.proposal.orderBeforeDate)}
                  onChange={(date) =>
                    handleInputChange({
                      target: { name: 'orderBeforeDate', value: date },
                    })
                  }
                  error={fieldErrors.orderBeforeDate}
                />
                <CustomDateInput
                  label="Delivery commencement date"
                  name="deliveryCommencementDate"
                  value={formatDateForDisplay(
                    btsSchool.proposal.deliveryCommencementDate,
                  )}
                  onChange={(date) =>
                    handleInputChange({
                      target: { name: 'deliveryCommencementDate', value: date },
                    })
                  }
                  error={fieldErrors.deliveryCommencementDate}
                />
                <CustomDateInput
                  label="Expected delivery date"
                  name="expectedDeliveryDate"
                  value={formatDateForDisplay(
                    btsSchool.proposal.expectedDeliveryDate,
                  )}
                  onChange={(date) =>
                    handleInputChange({
                      target: { name: 'expectedDeliveryDate', value: date },
                    })
                  }
                  error={fieldErrors.expectedDeliveryDate}
                />
                <Input
                  label="Username"
                  name="username"
                  value={btsSchool.proposal.username || ''}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  placeholder="Enter username"
                  className="mb-4"
                  error={fieldErrors.username}
                  autoComplete="new-password"
                />
                <Input
                  label="Password"
                  name="password"
                  value={btsSchool.proposal.password || ''}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  placeholder="Enter password"
                  type="password"
                  className="mb-4"
                  error={fieldErrors.password}
                  autoComplete="new-password"
                />
                <Input
                  label="Delivery Fee"
                  name="deliveryFee"
                  value={
                    btsSchool.proposal.deliveryFee !== undefined &&
                    btsSchool.proposal.deliveryFee !== null
                      ? btsSchool.proposal.deliveryFee
                      : ''
                  }
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  placeholder="Enter delivery fee"
                  type="number"
                  step="0.01"
                  className="mb-4"
                  error={fieldErrors.deliveryFee}
                />
              </div>
            </div>
            {formError && (
              <div className="col-span-2 text-red-500 text-sm ml-2">
                {formError}
              </div>
            )}
            <div className="col-span-2 flex justify-end gap-2 mt-4">
              <Button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-3xl">
                Next
              </Button>
            </div>
          </div>
        </form>
      </>
    );
  },
);

export default ProposalSchoolForm;
